import { Component, OnDestroy, OnInit } from '@angular/core'
import { sanitizeEmail } from '@@utils/sanitizeEmail'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { HttpClient } from '@angular/common/http'
import { ActivatedRoute } from '@angular/router'
import { TranslateService } from '@ngx-translate/core'
import { Subscription } from 'rxjs'
import { environment } from '../../../environments/environment'
import { LANGUAGE_DIRECTION_MAP, LanguageDirection } from '@@constants/language'

interface LeadFormField {
    visible: boolean
    required: boolean
    fieldType?: string
    options?: string[]
}

interface HowDidYouHearOption {
    value: string
    displayName: string
}

interface IdNameOption {
    id: number
    name: string
}

interface LeadFormConfig {
    isEnabled: boolean
    fields: { [key: string]: LeadFormField }
    childFields?: { [key: string]: LeadFormField }
    tenantName?: string
    tenantLogo?: string
    howDidYouHearOptions?: HowDidYouHearOption[]
    primaryColor?: string
    schoolOptions?: IdNameOption[]
    healthConditionOptions?: IdNameOption[]
    injuryOptions?: IdNameOption[]
}

// Additional fields that map to additionalInfo object
const ADDITIONAL_FIELD_KEYS = [
    'dateOfBirth', 'gender', 'bloodType', 'occupation', 'school', 'address',
    'emergencyContactName', 'phoneNumberEmergency', 'healthCond', 'injuries',
    'fatherName', 'motherName', 'fatherEmail', 'motherEmail',
    'fatherPhone', 'motherPhone', 'fatherOccupation', 'motherOccupation',
    'goal', 'level'
]

@Component({
    standalone: false,
    selector: 'app-public-lead-form',
    templateUrl: './public-lead-form.component.html',
    styleUrls: ['./public-lead-form.component.scss']
})
export class PublicLeadFormComponent implements OnInit, OnDestroy {
    leadForm!: FormGroup
    formConfig: LeadFormConfig | null = null
    loading = true
    submitting = false
    submitted = false
    error: string | null = null
    fieldErrors: { [key: string]: string } = {}
    /** A refused submission. Recoverable — shown above the form, never replacing it. */
    submitError: string | null = null
    onlineBookingId: string | null = null
    linkToken: string | null = null

    /** Whether the submitter is registering a child. No parent picker here — an anonymous
     *  submitter must not be able to search a tenant's families — so the server resolves the
     *  family from the address they type. */
    isChild = false

    howDidYouHearOptions: HowDidYouHearOption[] = []
    primaryColor = "#2196f3";
    showOtherInput = false

    // Dropdown options for FK-based fields
    schoolOptions: IdNameOption[] = []
    healthConditionOptions: IdNameOption[] = []
    injuryOptions: IdNameOption[] = []
    direction: 'rtl' | 'ltr' = 'ltr'

    private langChangeSubscription?: Subscription

    constructor(
        private fb: FormBuilder,
        private route: ActivatedRoute,
        private http: HttpClient,
        private translate: TranslateService
    ) {}

    ngOnInit(): void {
        this.updateDirection()
        this.langChangeSubscription = this.translate.onLangChange.subscribe(() => {
            this.updateDirection()
        })

        const isTokenLink = this.route.snapshot.data['isTokenLink']

        if (isTokenLink) {
            // Token-based link: /public/lead-form/:token
            this.linkToken = this.route.snapshot.paramMap.get('token')
            if (this.linkToken) {
                this.loadFormConfigByToken()
            } else {
                this.error = this.translatePublicForm('errors.invalid-form-link')
                this.loading = false
            }
        } else {
            // Direct link: /lead-form/:onlineBookingId
            this.onlineBookingId = this.route.snapshot.paramMap.get('onlineBookingId')
            this.linkToken = this.route.snapshot.queryParamMap.get('token')

            if (this.onlineBookingId) {
                this.loadFormConfig()
            } else {
                this.error = this.translatePublicForm('errors.invalid-form-link')
                this.loading = false
            }
        }
    }

    ngOnDestroy(): void {
        this.langChangeSubscription?.unsubscribe()
    }

    loadFormConfig(): void {
        const url = `${environment.apiUrl}/public/leads/form-config/${this.onlineBookingId}`
        this.http.get<{ message: string, entity: LeadFormConfig }>(url).subscribe({
            next: (response) => {
                this.formConfig = response.entity
                // Extract howDidYouHearOptions from the response if available
                if (response.entity.howDidYouHearOptions) {
                    this.howDidYouHearOptions = response.entity.howDidYouHearOptions
                }
                // Extract primaryColor from the response if available
                if (response.entity.primaryColor) {
                    this.primaryColor = response.entity.primaryColor
                }
                // Extract dropdown options
                if (response.entity.schoolOptions) {
                    this.schoolOptions = response.entity.schoolOptions
                }
                if (response.entity.healthConditionOptions) {
                    this.healthConditionOptions = response.entity.healthConditionOptions
                }
                if (response.entity.injuryOptions) {
                    this.injuryOptions = response.entity.injuryOptions
                }
                if (this.formConfig?.isEnabled) {
                    this.buildForm()
                }
                this.loading = false
            },
            error: () => {
                this.error = this.translatePublicForm('errors.unable-to-load')
                this.loading = false
            }
        })
    }

    loadFormConfigByToken(): void {
        const url = `${environment.apiUrl}/public/leads/form-config-by-token/${this.linkToken}`
        this.http.get<{ message: string, entity: LeadFormConfig }>(url).subscribe({
            next: (response) => {
                this.formConfig = response.entity
                // Extract howDidYouHearOptions from the response
                if (response.entity.howDidYouHearOptions) {
                    this.howDidYouHearOptions = response.entity.howDidYouHearOptions
                }
                // Extract primaryColor from the response
                if (response.entity.primaryColor) {
                    this.primaryColor = response.entity.primaryColor
                }
                // Extract dropdown options
                if (response.entity.schoolOptions) {
                    this.schoolOptions = response.entity.schoolOptions
                }
                if (response.entity.healthConditionOptions) {
                    this.healthConditionOptions = response.entity.healthConditionOptions
                }
                if (response.entity.injuryOptions) {
                    this.injuryOptions = response.entity.injuryOptions
                }
                if (this.formConfig?.isEnabled) {
                    this.buildForm()
                }
                this.loading = false
            },
            error: () => {
                this.error = this.translatePublicForm('errors.unable-to-load')
                this.loading = false
            }
        })
    }

    /** The configuration for the selected type — the one accessor every read goes through. */
    get activeFields(): { [key: string]: LeadFormField } {
        if (!this.formConfig) return {}
        const config = this.isChild ? this.formConfig.childFields : this.formConfig.fields
        // See the staff form: childFields is absent until the backend ships, and {} renders a form
        // with no fields at all.
        return config || this.formConfig.fields || {}
    }

    /** Switching type rebuilds the form against the other configuration, keeping shared values. */
    onLeadTypeChange(isChild: boolean): void {
        if (this.isChild === isChild) return
        const previousValues = this.leadForm ? { ...this.leadForm.value } : {}
        this.isChild = isChild
        this.buildForm()
        for (const controlName of Object.keys(previousValues)) {
            const control = this.leadForm.get(controlName)
            if (control && previousValues[controlName] !== null && previousValues[controlName] !== '') {
                control.setValue(previousValues[controlName])
            }
        }
        this.fieldErrors = {}
    }

    buildForm(): void {
        if (!this.formConfig) return

        const fields = this.activeFields
        const formControls: any = {}

        // Core fields
        if (fields['name']?.visible) {
            formControls['firstName'] = ['', fields['name'].required ? Validators.required : []]
            formControls['lastName'] = ['', fields['name'].required ? Validators.required : []]
        }

        if (fields['email']?.visible) {
            formControls['email'] = ['', this.isFieldRequired('email') ? [Validators.required, Validators.email] : [Validators.email]]
        }

        if (fields['phoneNumber']?.visible) {
            formControls['phoneNumber'] = ['', fields['phoneNumber'].required ? Validators.required : []]
        }

        if (fields['howDidYouHear']?.visible) {
            formControls['howDidYouHear'] = ['', fields['howDidYouHear'].required ? Validators.required : []]
            formControls['howDidYouHearOther'] = ['']
        }

        if (fields['notes']?.visible) {
            formControls['notes'] = ['', fields['notes'].required ? Validators.required : []]
        }

        // Additional fields
        for (const fieldKey of ADDITIONAL_FIELD_KEYS) {
            const fieldConfig = fields[fieldKey]
            if (fieldConfig?.visible) {
                const validators = fieldConfig.required ? [Validators.required] : []
                // Add email validator for email fields
                if (fieldConfig.fieldType === 'email') {
                    validators.push(Validators.email)
                }
                
                // Handle FK-based dropdown fields
                if (fieldKey === 'school' && this.schoolOptions.length > 0) {
                    formControls['idSchool'] = ['', validators]
                } else if (fieldKey === 'healthCond' && this.healthConditionOptions.length > 0) {
                    formControls['healthConditionIds'] = [[], validators]
                } else if (fieldKey === 'injuries' && this.injuryOptions.length > 0) {
                    formControls['injuryIds'] = [[], validators]
                } else {
                    formControls[fieldKey] = ['', validators]
                }
            }
        }

        this.leadForm = this.fb.group(formControls)
    }

    getVisibleAdditionalFields(): string[] {
        return ADDITIONAL_FIELD_KEYS.filter(key => this.activeFields[key]?.visible)
    }

    getFieldConfig(fieldKey: string): LeadFormField | undefined {
        return this.activeFields[fieldKey]
    }

    isFkDropdownField(fieldKey: string): boolean {
        return (fieldKey === 'school' && this.schoolOptions.length > 0) ||
               (fieldKey === 'healthCond' && this.healthConditionOptions.length > 0) ||
               (fieldKey === 'injuries' && this.injuryOptions.length > 0)
    }

    getFieldLabel(fieldKey: string): string {
        return this.translate.instant(`portal.leads.public-form.fields.${fieldKey}`)
    }

    getFieldPlaceholder(fieldKey: string): string {
        return this.translatePublicForm('placeholders.enter-field', {
            field: this.getFieldLabel(fieldKey)
        })
    }

    translatePublicForm(key: string, params?: Record<string, unknown>): string {
        return this.translate.instant(`portal.leads.public-form.${key}`, params)
    }

    private updateDirection(): void {
        const currentLang = this.translate.currentLang || this.translate.defaultLang || 'en'
        this.direction = LANGUAGE_DIRECTION_MAP[currentLang] === LanguageDirection.RTL
            ? 'rtl'
            : 'ltr'
    }

    onHowDidYouHearChange(value: string): void {
        this.showOtherInput = value === 'OTHERS'
        if (!this.showOtherInput) {
            this.leadForm.get('howDidYouHearOther')?.setValue('')
        }
    }

    onSubmit(): void {
        if (this.leadForm.invalid) {
            this.leadForm.markAllAsTouched()
            return
        }

        // The staff form has had this since the start; this is the surface the reported bug actually
        // came in on. Caught here rather than left to the server so the person is told which two
        // fields clash while both are still in front of them.
        // The child defaults mark both parent emails visible but NOT required — "at least one of
        // them" cannot be said with per-field flags — so nothing else stops a blank pair reaching
        // the server. The staff form has always had this check; this is the surface the reported
        // leads were lost on.
        if (this.isChild && !this.hasAContactableParent()) {
            this.fieldErrors = {
                fatherEmail: this.translatePublicForm('errors.child-requires-parent-email')
            }
            this.leadForm.get('fatherEmail')?.markAsTouched()
            this.leadForm.get('motherEmail')?.markAsTouched()
            return
        }

        if (this.isChild && this.childEmailClashesWithParent()) {
            this.fieldErrors = {
                email: this.translatePublicForm('errors.child-email-same-as-parent')
            }
            this.leadForm.get('email')?.markAsTouched()
            return
        }

        this.submitting = true
        this.error = null
        this.submitError = null
        this.fieldErrors = {}
        
        const formValue = this.leadForm.value
        const formData: any = {
            onlineBookingId: this.onlineBookingId,
            linkToken: this.linkToken,
            email: formValue.email,
            phoneNumber: formValue.phoneNumber,
            howDidYouHear: formValue.howDidYouHear,
            howDidYouHearOther: formValue.howDidYouHearOther,
            notes: formValue.notes,
            isChild: this.isChild
        }

        // Combine firstName and lastName into name
        if (formValue.firstName || formValue.lastName) {
            formData.name = [formValue.firstName, formValue.lastName].filter(Boolean).join(' ')
        }

        // Build additionalInfo object from additional fields
        const additionalInfo: any = {}
        for (const fieldKey of ADDITIONAL_FIELD_KEYS) {
            if (formValue[fieldKey] !== undefined && formValue[fieldKey] !== '') {
                additionalInfo[fieldKey] = formValue[fieldKey]
            }
        }
        
        // Handle FK-based dropdown fields
        if (formValue.idSchool) {
            additionalInfo.idSchool = parseInt(formValue.idSchool, 10)
        }
        if (formValue.healthConditionIds && formValue.healthConditionIds.length > 0) {
            additionalInfo.healthConditionIds = formValue.healthConditionIds.map((id: string) => parseInt(id, 10))
        }
        if (formValue.injuryIds && formValue.injuryIds.length > 0) {
            additionalInfo.injuryIds = formValue.injuryIds.map((id: string) => parseInt(id, 10))
        }
        
        if (Object.keys(additionalInfo).length > 0) {
            formData.additionalInfo = additionalInfo
        }

        const url = `${environment.apiUrl}/public/leads`
        this.http.post(url, formData).subscribe({
            next: () => {
                this.submitting = false
                this.submitted = true
            },
            error: (err) => {
                this.submitting = false
                const errorMessage = err.error?.message || err.error?.description || ''
                
                // Map backend error messages to field names
                this.fieldErrors = this.mapErrorToField(errorMessage)
                
                // submitError, NOT error: `error` is the fatal load-failure channel and the template
                // swaps the whole form out for the "Oops" panel when it is set — so an unmapped
                // rejection used to destroy everything the person had typed. A submission that the
                // server refused is recoverable: keep the form, show the reason above it.
                if (Object.keys(this.fieldErrors).length === 0) {
                    this.submitError = errorMessage || this.translatePublicForm('errors.submit-failed')
                }
            }
        })
    }

    /**
     * Maps backend error messages to specific form fields
     */
    private mapErrorToField(errorMessage: string): { [key: string]: string } {
        const fieldErrors: { [key: string]: string } = {}
        
        // Map error messages to field names
        if (errorMessage.includes('Email is required') || errorMessage.includes('البريد الإلكتروني مطلوب') || 
            errorMessage.includes('L\'email est requis') || errorMessage.includes('ईमेल आवश्यक है')) {
            fieldErrors['email'] = errorMessage
        } else if (errorMessage.includes('valid email') || errorMessage.includes('بريد إلكتروني صالح') || 
                   errorMessage.includes('email valide') || errorMessage.includes('मान्य ईमेल')) {
            fieldErrors['email'] = errorMessage
        } else if (errorMessage.includes('Phone number is required') || errorMessage.includes('رقم الهاتف مطلوب') || 
                   errorMessage.includes('numéro de téléphone est requis') || errorMessage.includes('फ़ोन नंबर आवश्यक है')) {
            fieldErrors['phoneNumber'] = errorMessage
        } else if (errorMessage.includes('Name is required') || errorMessage.includes('الاسم مطلوب') || 
                   errorMessage.includes('nom est requis') || errorMessage.includes('नाम आवश्यक है')) {
            fieldErrors['firstName'] = errorMessage
        } else if (errorMessage.includes('how you heard') || errorMessage.includes('كيف سمعت عنا') || 
                   errorMessage.includes('comment vous avez entendu') || errorMessage.includes('हमारे बारे में कैसे सुना')) {
            fieldErrors['howDidYouHear'] = errorMessage
        } else if (errorMessage.includes('specify how you heard') || errorMessage.includes('تحديد كيف سمعت') || 
                   errorMessage.includes('préciser comment') || errorMessage.includes('निर्दिष्ट करें')) {
            fieldErrors['howDidYouHearOther'] = errorMessage
        }
        
        return fieldErrors
    }

    getFieldError(fieldName: string): string | null {
        return this.fieldErrors[fieldName] || null
    }

    hasFieldError(fieldName: string): boolean {
        return !!this.fieldErrors[fieldName]
    }

    /** See the staff form: cleaned on blur, because the format validator runs before submit. */
    sanitizeEmailField(fieldName: string): void {
        const control = this.leadForm?.get(fieldName)
        if (!control) {
            return
        }
        const cleaned = sanitizeEmail(control.value)
        if (cleaned !== control.value) {
            control.setValue(cleaned)
        }
    }

    /**
     * True when the child's own address is also one of the parent addresses on the form.
     *
     * sanitizeEmail on all three, so a value pasted with a non-breaking space cannot slip past and
     * land on the generic server rejection instead of this message.
     */
    /** At least one parent email — the rule the server enforces, checked before we ask it. */
    private hasAContactableParent(): boolean {
        const raw = this.leadForm.getRawValue()
        return !!(sanitizeEmail(raw.fatherEmail) || sanitizeEmail(raw.motherEmail))
    }

    private childEmailClashesWithParent(): boolean {
        const raw = this.leadForm.getRawValue()
        const own = sanitizeEmail(raw.email)
        if (!own) {
            return false
        }
        return [raw.fatherEmail, raw.motherEmail].some(parentEmail => sanitizeEmail(parentEmail) === own)
    }

    isFieldVisible(fieldName: string): boolean {
        return this.activeFields[fieldName]?.visible ?? false
    }

    isFieldRequired(fieldName: string): boolean {
        // A child always needs an address of its own — see isEmailRequired on the staff form.
        if (this.isChild && fieldName === 'email') {
            return true
        }
        return this.activeFields[fieldName]?.required ?? false
    }
}
